import { CfiUtils, CfiGenerator, CfiParser } from '@redshelf/redshelf-cfi';

const environment = process.env.REACT_APP_NODE_ENV || process.env.NODE_ENV;

const cfiOptions = {
  /**
   * We can't change blacklistSelectors unless
   * a) new nodes are being injected into the html of previously processed books and we need to ignore them or
   * b) nodes are being removed during processing and are no longer relevant because they don't exist in any title.
   *
   * For example: we start injecting google analytics into every document and need the cfi parser to ignore it,
   * or we decide we no longer want rs-auto-pagebreak and process them out of all books (rare and unlikely scenario)
   *
   * If we were to remove, for example, `.rs-auto-pagebreak` while the nodes still exist in the books,
   * it would breaks all user content CFI positioning in books with redshelf-autogenerated page numbers.
   */
  blacklistSelectors: [
    '.rs-inline-boundary',
    '.rs-inline-display',
    '.rs-inline-button',
    '.rs-text-marker',
    '.MathJax_Preview',
    '.MathJax_CHTML',
    '.mjx-chtml',
    '#MathJax_Message',
    'script[type="math/mm"]',
    '.rs-cfi-ignore',
    'html > :not(body):not(head)',
    '.rs-auto-pagebreak',
    '.svgOverElement',
  ],
};
const HORIZONTAL_SEARCH_STEP = 100;
const VERTICAL_SEARCH_STEP = 10;

const isBlacklisted = (node) => {
  let cfiUtils = new CfiUtils(cfiOptions);
  return cfiUtils.isBlacklisted(node);
};

const formatCfi = (cfi) => {
  return `epubcfi(${cfi})`;
};

const getSnippetForRange = (range) => {
  let documentFragment = range.cloneContents();
  documentFragment
    .querySelectorAll('br.rs-cfi-ignore[role=presentation]')
    .forEach((br) => br.replaceWith(' '));
  let snippet = documentFragment.textContent;

  if (!snippet.length && range.startContainer.parentNode) {
    snippet = range.startContainer.parentNode.innerText;
  }

  return snippet;
};

const getSnippetForCfi = (contentDocument, cfi) => {
  const range = cfiLogic.getRangeForCfi(contentDocument, cfi);
  return getSnippetForRange(range);
};

const isCfiARange = (cfi) => {
  //when cfi is actual cfi range
  let parts = cfi.split(',');

  return parts.length > 1;
};

const isCfiAnElement = (cfi) => {
  //when cfi points to an element
  let parts = cfi.split(',');

  return parts.length <= 1;
};

const getRangeForCfi = (contentDocument, cfi) => {
  try {
    if (cfiLogic.isCfiAnElement(cfi)) {
      let el = cfiLogic.getElementForCfi(contentDocument, cfi);
      let range = contentDocument.createRange();
      range.selectNodeContents(el);

      return range;
    }

    if (cfiLogic.isCfiARange(cfi)) {
      let cfiParser = new CfiParser(contentDocument, cfiOptions);
      let rangeParts = cfiParser.getRangeByCfi(formatCfi(cfi));
      let range = contentDocument.createRange();

      range.setStart(rangeParts.anchorElement, rangeParts.anchorOffset);
      range.setEnd(rangeParts.focusElement, rangeParts.focusOffset);

      return range;
    }
  } catch (e) {
    if (environment !== 'test') {
      console.warn(`No range found in document for cfi: ${cfi}`);
    }

    return contentDocument.createRange();
  }
};

const getElementForCfi = (contentDocument, cfi) => {
  // Ensure that it is not a range, and even if it's not, remove ID assertions that are deep in the path, which break rs-cfi
  cfi = cfiLogic.splitCfiRange(cfi).start;
  let formattedCfi = cfiLogic.formatCfi(cfi);
  let cfiParser = new CfiParser(contentDocument, cfiOptions);
  let elemTargetWithOffset = cfiParser.getElementByCfi(formattedCfi);
  let node = elemTargetWithOffset.node;
  if (node && node.nodeType === Node.TEXT_NODE) {
    node = node.parentNode;
  }
  return node;
};

const getCfiForSelection = (contentDocument, selection) => {
  let cfiGenerator = new CfiGenerator(contentDocument, cfiOptions);
  return cfiGenerator.getBySelection(selection);
};

const isSelectionValid = (contentDocument, selection) => {
  return (
    selection.focusNode !== contentDocument.body &&
    selection.anchorNode !== contentDocument.body
  );
};

const createCfiRange = (contentDocument, start, end) => {
  return cfiLogic.mergeCfis(
    contentDocument,
    cfiLogic.formatCfi(start),
    cfiLogic.formatCfi(end),
  );
};

const splitCfiRange = (cfi) => {
  let parts = cfi.split(',');
  let start = parts[0];
  let end = parts[0];

  if (parts.length > 1) {
    start = `${parts[0]}${parts[1]}`;
    end = `${parts[0]}${parts[2]}`;
  }

  return {
    start,
    end,
  };
};

const mergeCfis = (contentDocument, start, end) => {
  let cfiGenerator = new CfiGenerator(contentDocument, cfiOptions);
  return cfiGenerator.mergeCfisToRange(
    formatCfi(start),
    cfiLogic.formatCfi(end),
  );
};

const mergeCfiRanges = (contentDocument, cfiRanges) => {
  let cfis = [];

  cfiRanges.forEach((cfi) => {
    let parts = cfiLogic.splitCfiRange(cfi);
    cfis.push([parts.start, parts.end]);
  });

  let cfiUtils = new CfiUtils(contentDocument, cfiOptions);
  let sortedCfis = cfiUtils.sort(cfis);

  return cfiLogic.mergeCfis(
    contentDocument,
    sortedCfis.firstObject,
    sortedCfis.lastObject,
  );
};

const doCfiRangesIntersect = (contentDocument, first, second) => {
  let firstRange = cfiLogic.getRangeForCfi(contentDocument, first);
  let secondRange = cfiLogic.getRangeForCfi(contentDocument, second);

  let a = [
    firstRange.compareBoundaryPoints(Range.START_TO_START, secondRange),
    firstRange.compareBoundaryPoints(Range.START_TO_END, secondRange),
    firstRange.compareBoundaryPoints(Range.END_TO_START, secondRange),
    firstRange.compareBoundaryPoints(Range.END_TO_END, secondRange),
  ];

  let unique = [...new Set(a)];

  return unique.length !== 1 || a[0] === 0;
};

const compareCfiRanges = (contentDocument, a, b) => {
  if (a === null && b === null) {
    return 0;
  }

  if (a === null) {
    return -1;
  }

  if (b === null) {
    return 1;
  }

  let cfiUtils = new CfiUtils(contentDocument, cfiOptions);
  let firstCfi = cfiLogic.formatCfi(cfiLogic.splitCfiRange(a).start);
  let secondCfi = cfiLogic.formatCfi(cfiLogic.splitCfiRange(b).start);

  // a valid cfi should be considered 'greater than' an invalid one.
  if (!cfiUtils.isValidCfi(firstCfi) && !cfiUtils.isValidCfi(secondCfi)) {
    return 0;
  }

  if (!cfiUtils.isValidCfi(firstCfi)) {
    return -1;
  }

  if (!cfiUtils.isValidCfi(secondCfi)) {
    return 1;
  }

  return cfiUtils.compare(firstCfi, secondCfi);
};

const getCfiForElement = (contentDocument, element) => {
  if (typeof element === 'string') {
    //allow passing of an id selector or the element
    element = contentDocument.getElementById(element);
    if (!element) {
      return;
    }
  }
  let cfiGenerator = new CfiGenerator(contentDocument, cfiOptions);
  return cfiGenerator.getByElement(element);
};

const getCfiForRange = (contentDocument, range) => {
  let validRange = cfiLogic._validateRange(contentDocument, range);
  let sel = _createSelectionObjectFromRange(validRange);
  let cfi = getCfiForSelection(contentDocument, sel);
  return cfi;
};

// only exported for testing purposes
const _validateRange = (contentDocument, range) => {
  let cfiUtils = new CfiUtils(cfiOptions);
  if (
    cfiUtils.isBlacklisted(range.startContainer) ||
    cfiUtils.isBlacklisted(range.endContainer)
  ) {
    let parentRange = contentDocument.createRange();
    let relativeEl = findNearestRelativeWithContent(
      range.startContainer.parentElement,
    );
    parentRange.selectNodeContents(relativeEl);
    return cfiLogic._validateRange(contentDocument, parentRange);
  }

  return range;
};

const _manualElementSearch = (contentDocument, top) => {
  let width = contentDocument.body.getBoundingClientRect().width;
  let height = contentDocument.body.getBoundingClientRect().height;

  let highlights = contentDocument.querySelectorAll('.rs-inline-display');

  let hiddenClass = 'rs-disable-pointer-events';
  highlights.forEach((highlight) => {
    highlight.classList.add(hiddenClass);
  });

  for (
    var y_search = top;
    y_search < height;
    y_search += VERTICAL_SEARCH_STEP
  ) {
    for (
      var x_search = 0;
      x_search < width;
      x_search += HORIZONTAL_SEARCH_STEP
    ) {
      let el = contentDocument.elementFromPoint(x_search, y_search);
      if (!el) {
        continue;
      }

      if (
        el.nodeName.toLowerCase() !== 'body' &&
        el.nodeName.toLowerCase() !== 'html'
      ) {
        highlights.forEach((highlight) => {
          highlight.classList.remove(hiddenClass);
        });
        return el;
      }
    }
  }

  //no elements found
  highlights.forEach((highlight) => {
    highlight.classList.remove(hiddenClass);
  });

  return contentDocument.body;
};

const getPositionForCfiRange = (contentDocument, cfis, cfi) => {
  let parts = cfiLogic.splitCfiRange(cfi);
  let cfiStart = cfiLogic.formatCfi(parts.start);
  let cfiUtils = new CfiUtils(contentDocument, cfiOptions);

  var formattedCfis = cfis.map((cfi) => {
    if (cfi) {
      return cfiLogic.formatCfi(cfi);
    }

    console.error('Must provide a list of cfis to sort function.');

    return null;
  });

  formattedCfis.push(cfiStart);

  let sortedCfis = cfiUtils.sort(formattedCfis);
  return sortedCfis.indexOf(cfiStart) - 1; //return the index of the preceding cfi
};

const findNearestRelativeWithContent = (element) => {
  if (elementHasContent(element)) {
    return element;
  }

  if (element.nextElementSibling) {
    if (element.nextElementSibling.textContent.trim().length) {
      return element.nextElementSibling;
    }

    return findNearestRelativeWithContent(element.nextElementSibling);
  }

  if (element.parentElement) {
    if (element.parentElement.textContent.trim().length) {
      return element.parentElement;
    }

    return findNearestRelativeWithContent(element.parentElement);
  }

  return element;
};

const elementHasContent = (element) => {
  return (
    element.textContent.length ||
    element.getAttribute('alt') ||
    element.getAttribute('aria-label') ||
    element.getAttribute('aria-labelledby')
  );
};

const _createSelectionObjectFromRange = (range) => {
  let sel = {
    anchorNode: range.startContainer,
    focusNode: range.endContainer,
    anchorOffset: range.startOffset,
    focusOffset: range.endOffset,
  };

  return sel;
};

const isValidCfi = (cfi) => {
  let cfiUtils = new CfiUtils();
  return cfiUtils.isValidCfi(formatCfi(cfi));
};

const cfiLogic = {
  formatCfi,
  getSnippetForCfi,
  isBlacklisted,
  isCfiARange,
  isCfiAnElement,
  isValidCfi,
  getRangeForCfi,
  getElementForCfi,
  getCfiForSelection,
  getSnippetForRange,
  isSelectionValid,
  createCfiRange,
  splitCfiRange,
  mergeCfis,
  mergeCfiRanges,
  doCfiRangesIntersect,
  compareCfiRanges,
  getCfiForElement,
  getCfiForRange,
  _validateRange, // only exported for testing purposes
  _manualElementSearch, // only exported for testing purposes
  getPositionForCfiRange,
  findNearestRelativeWithContent,
  elementHasContent,
};

export default cfiLogic;
